import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import useNotification from "../../../../../../shared/hooks/useNotification";

export interface IInputTcProductStandardDetails {
  productNo?: string;
  standard?: string;               // New field for ATC standard from response
  standardLabelGrade: string;      // Label grade (can be empty for ATC)
  certifiedWeight: string;
  certifiedWeightUsed: string;
}

interface InputTcProductStandardDetailsProps {
  productStandardData: IInputTcProductStandardDetails[];
  setProductStandardData: React.Dispatch<
    React.SetStateAction<IInputTcProductStandardDetails[]>
  >;
  selectedInputTcId: string;
  selectedProductIds: string[];
  isTC?: boolean | null;
}

// Define Yup validation schema for each row
const validationSchema = Yup.object().shape({
  standard: Yup.string().nullable(),
  standardLabelGrade: Yup.string().nullable(),
  certifiedWeight: Yup.string()
    .test(
      'is-exactly-two-decimal-places',
      'Number must be a valid decimal number with upto 2 decimal places.',
      (value: string | undefined) => {
        if (!value) return true; // Allow empty values for required validation to handle
        return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
      }
    )
    .typeError("Certified Weight must be a valid number"),
  certifiedWeightUsed: Yup.string()
    .test(
      'is-exactly-two-decimal-places',
      'Number must be a valid decimal number with upto 2 decimal places.',
      (value: string | undefined) => {
        if (!value) return true; // Allow empty values for required validation to handle
        return /^(0|[1-9]\d*|0\.\d{1,2}|[1-9]\d*\.\d{1,2})$/.test(value); // Match exactly two decimal places
      }
    )
    .typeError("Certified Weight Used must be a valid number"),
});

const InputTcProductStandardDetails: React.FC<InputTcProductStandardDetailsProps> = ({
  productStandardData,
  setProductStandardData,
  selectedInputTcId,
  selectedProductIds,
  isTC,
}) => {
  const filterOutGOTSRows = (rows: IInputTcProductStandardDetails[]) =>
    rows.filter((row) => (row.standard || "").toUpperCase() !== "GOTS");

  const [data, setData] = useState<IInputTcProductStandardDetails[]>(filterOutGOTSRows(productStandardData));
  const [tableData, setTableData] = useState<IInputTcProductStandardDetails[]>(filterOutGOTSRows(productStandardData));
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("input-tc-product-details");
  const [labelGrades, setLabelGrades] = useState<{ value: string; label: string; }[]>([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave, getLabelGrades } = useTransactionCertificateStore();
  const { executeGet } = useApi();
  const { notify } = useNotification();
  const hasFetchedRef = useRef(false);

  const formik = useFormik<IInputTcProductStandardDetails>({
    initialValues: {
      standard: "",
      standardLabelGrade: "",
      certifiedWeight: "",
      certifiedWeightUsed: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });
  useEffect(() => {
    setLabelGrades(getLabelGrades);

  }, [])
  useEffect(() => {
    updateField("tc-std-info", data)
  }, [data])

  const getStandardLabelGradeOptions = (standard?: string) => {
    if (!standard) return labelGrades;

    const normalized = standard.toUpperCase();

    return labelGrades.filter((option) => {
      const value = ((option?.value || "") as string).toString().toUpperCase();
      const label = ((option?.label || "") as string).toString().toUpperCase();
      const hasStandard = value.includes(normalized) || label.includes(normalized);
      const hasNoCertifiedRm = label.includes("COMPONENT DOES NOT HAVE ANY CERTIFIED RM");
      return hasStandard || hasNoCertifiedRm;
    });
  };

  // Reset fetch flag when parameters change
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [selectedInputTcId, selectedProductIds]);

  // Fetch standard details when component mounts and we have the required data
  useEffect(() => {
    const fetchStandardDetails = async () => {
      // Don't call TC-related APIs if ATC is selected
      if (isTC === false) {
        return;
      }
      
      // Skip API call if we already have data (edit flow)
      if (productStandardData && productStandardData.length > 0) {
        const filteredData = filterOutGOTSRows(productStandardData);
        setData(filteredData);
        setTableData(filteredData);
        return;
      }

      if (!selectedInputTcId || selectedProductIds.length === 0 || hasFetchedRef.current) {
        return;
      }

      hasFetchedRef.current = true;

      try {
        // Build URL with multiple productIds parameters
        const baseUrl = `${TCAppConfig.GET_STD_DETAILS_PDCT_INFO}?tcId=${selectedInputTcId}`;
        const productIdsParams = selectedProductIds.map(id => `&productIds=${id}`).join('');
        const url = `${baseUrl}${productIdsParams}`;
        const response: any = await executeGet(url);

        if (response?.data && Array.isArray(response.data)) {
          // Map the response data to our interface format
          const mappedData: IInputTcProductStandardDetails[] = [];
          
          response.data.forEach((productData: any) => {
            if (productData.tcProductStandardInformation && Array.isArray(productData.tcProductStandardInformation)) {
              productData.tcProductStandardInformation.forEach((standardInfo: any) => {
                mappedData.push({
                  productNo: productData.tcProductNo || "",
                  standard: "", // TC path has no external standard column value here
                  standardLabelGrade: standardInfo.tcProductStandardLabelGrade || "",
                  certifiedWeight: standardInfo.tcProductCertifiedWeight?.toString() || "",
                  certifiedWeightUsed: ""
                });
              });
            }
          });

          // Update the data state
          setData(mappedData);
          setTableData(mappedData);
          setProductStandardData(mappedData);
        }
      } catch (err) {
        console.error("Failed to fetch standard details:", err);
        notify("Failed to fetch standard details", "error");
        hasFetchedRef.current = false; // Reset on error so it can retry
      }
    };

    fetchStandardDetails();
  }, [selectedInputTcId, selectedProductIds, productStandardData]);
  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };


  const handleCancelEdit = async () => {
    if(editingRowIndex!==-1){
      setData(tableData);
    }
    // const currentRowValues = formik.values;
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;
    if (formik.values.certifiedWeight == "" && formik.values.certifiedWeightUsed == "" && formik.values.standardLabelGrade == "") {
      return;
    }
    const updatedRow = formik.values;
    const newData = filterOutGOTSRows(data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      ));
    setData(newData);
    setTableData(newData);
    setRestrictPopUpSave(false);
    setProductStandardData(newData);
    setEditingRowIndex(-1);
    formik.resetForm();
  };


  const allColumns: EditableColumnDef<IInputTcProductStandardDetails>[] = [
    {
      accessorKey: "productNo",
      header: "Product No",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="productNo"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product No"
          size="small"
        />
      ),
    },
    {
      accessorKey: "standard",
      header: "Standard",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="standard"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Standard"
          size="small"
        />
      ),
    },
    {
      accessorKey: "standardLabelGrade",
      header: "Standard Label Grade",
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        const option = labelGrades.find((opt) => opt.value === value);
        return <span>{option?.label || value || ""}</span>;
      },
      renderEditCell: ({ value, onChange, rowIndex }) => {
        const selectedStandard = data?.[rowIndex]?.standard;
        return (
          <CustomSelect
            label=""
            name="standardLabelGrade"
            value={value}
            enableSearch={true}
            placeholder="Select Standard Label Grade"
            options={getStandardLabelGradeOptions(selectedStandard)}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      },
    },
    {
      accessorKey: "certifiedWeight",
      header: "TC Certified Weight (kg)",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          sx={{ mb: 2 }}
          name="certifiedWeight"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === '') {
              onChange(newValue);
            }
          }}
          placeholder="Enter value"
        />
      ),
    },
    {
      accessorKey: "certifiedWeightUsed",
      header: "Certified Weight Used (kg)",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          sx={{ mb: 2 }}
          name="certifiedWeightUsed"
          value={value}
          onChange={(e) => {
            const newValue = e.target.value;
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === '') {
              onChange(newValue);
            }
          }}
          placeholder="Enter value"
        />
      ),
    },
  ];


  // Apply condition here
  const columns = isTC
    ? allColumns.filter(
        (col) =>
          col.accessorKey !== "standard"
      )
    : allColumns;

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setProductStandardData(updatedData);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          hideDelete
        />
      </Box>
    </div>
  );
};

export default InputTcProductStandardDetails;
